import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Bell, AlertCircle, CheckCheck, Inbox } from 'lucide-react';
import { formatDistanceToNow, parseISO } from 'date-fns';

import api from '../api/axios';
import LoadingSpinner from '../components/common/LoadingSpinner';

// ── Helpers ───────────────────────────────────────────────────────────────────

function getNotificationLink(notif) {
  // Explicit deep link stored with the notification (e.g. /availability?period=12)
  if (notif.link && notif.link.startsWith('/') && !notif.link.startsWith('//')) return notif.link;
  const t = notif.title.toLowerCase();
  if (
    t.includes('sick') ||
    t.includes('cover') ||
    t.includes('swap') ||
    t.includes('roster') ||
    t.includes('shift')
  ) return '/roster';
  if (t.includes('availability') || t.includes('reminder')) return '/availability';
  return null;
}

function dispatchReadEvent() {
  window.dispatchEvent(new CustomEvent('notificationsRead'));
}

// ── NotificationsPage ─────────────────────────────────────────────────────────

function NotificationsPage() {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [marking, setMarking] = useState(false);

  const fetchNotifications = useCallback(async () => {
    try {
      const res = await api.get('/notifications');
      setNotifications(res.data);
    } catch {
      toast.error('Failed to load notifications.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchNotifications();
    // Tell Navbar to refresh its unread count when this page mounts
    dispatchReadEvent();
  }, [fetchNotifications]);

  const handleClick = async (notif) => {
    if (!notif.is_read) {
      try {
        await api.post(`/notifications/${notif.id}/read`);
        setNotifications((prev) =>
          prev.map((n) => (n.id === notif.id ? { ...n, is_read: 1 } : n))
        );
        dispatchReadEvent();
      } catch {
        // ignore — still navigate
      }
    }
    const link = getNotificationLink(notif);
    if (link) navigate(link);
  };

  const handleMarkAll = async () => {
    setMarking(true);
    try {
      await api.post('/notifications/read-all');
      setNotifications((prev) => prev.map((n) => ({ ...n, is_read: 1 })));
      dispatchReadEvent();
    } catch {
      toast.error('Failed to mark all as read.');
    } finally {
      setMarking(false);
    }
  };

  const unreadCount = notifications.filter((n) => !n.is_read).length;

  return (
    <div>
      {/* Header */}
      <div className="page-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <Bell size={22} strokeWidth={2} style={{ color: 'var(--primary-dark)' }} />
          <div>
            <h1 className="page-title">Notifications</h1>
            {!loading && (
              <p className="page-subtitle">
                {unreadCount > 0 ? `${unreadCount} unread` : 'All caught up'}
              </p>
            )}
          </div>
        </div>
        <button
          className="btn btn-secondary"
          onClick={handleMarkAll}
          disabled={marking || unreadCount === 0}
        >
          <CheckCheck size={16} />
          Mark all as read
        </button>
      </div>

      {/* Content */}
      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '48px' }}>
          <LoadingSpinner />
        </div>
      ) : notifications.length === 0 ? (
        <div
          style={{
            background: 'var(--white)',
            borderRadius: 'var(--radius-lg)',
            border: '2px dashed var(--border)',
            padding: '64px 24px',
            textAlign: 'center',
            color: 'var(--text-muted)',
          }}
        >
          <Inbox size={40} style={{ marginBottom: '12px', opacity: 0.4 }} />
          <p style={{ fontSize: '1rem', fontWeight: 500 }}>No notifications yet</p>
          <p style={{ fontSize: '0.85rem', marginTop: '4px' }}>
            You'll see updates here as activity happens.
          </p>
        </div>
      ) : (
        <div
          style={{
            background: 'var(--white)',
            borderRadius: 'var(--radius-lg)',
            border: '1px solid var(--border)',
            overflow: 'hidden',
          }}
        >
          {notifications.map((notif, idx) => {
            const isUnread = !notif.is_read;
            const isUrgent = notif.type === 'urgent';
            const hasLink = !!getNotificationLink(notif);

            return (
              <div
                key={notif.id}
                onClick={() => handleClick(notif)}
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '12px',
                  padding: '16px 20px',
                  borderBottom: idx < notifications.length - 1 ? '1px solid var(--border)' : 'none',
                  borderLeft: isUnread ? '3px solid var(--primary)' : '3px solid transparent',
                  background: isUnread ? 'var(--primary-xlight)' : 'var(--white)',
                  cursor: hasLink || isUnread ? 'pointer' : 'default',
                  transition: 'var(--transition)',
                }}
                onMouseEnter={(e) => {
                  if (hasLink || isUnread) e.currentTarget.style.background = 'var(--bg)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = isUnread ? 'var(--primary-xlight)' : 'var(--white)';
                }}
              >
                {/* Icon */}
                <div style={{ paddingTop: '2px', flexShrink: 0 }}>
                  {isUrgent ? (
                    <AlertCircle size={18} style={{ color: 'var(--danger)' }} />
                  ) : (
                    <Bell size={18} style={{ color: 'var(--primary-dark)' }} />
                  )}
                </div>

                {/* Body */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div
                    style={{
                      fontWeight: isUnread ? 600 : 400,
                      fontSize: '0.9rem',
                      color: 'var(--text)',
                      marginBottom: '4px',
                    }}
                  >
                    {notif.title}
                  </div>
                  <div
                    style={{
                      fontSize: '0.85rem',
                      color: 'var(--text-light)',
                      lineHeight: 1.4,
                    }}
                  >
                    {notif.body}
                  </div>
                </div>

                {/* Time + unread dot */}
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-end',
                    gap: '6px',
                    flexShrink: 0,
                  }}
                >
                  <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>
                    {formatDistanceToNow(parseISO(notif.created_at), { addSuffix: true })}
                  </span>
                  {isUnread && (
                    <span
                      style={{
                        width: '8px',
                        height: '8px',
                        borderRadius: '50%',
                        background: 'var(--primary)',
                        display: 'block',
                      }}
                    />
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default NotificationsPage;
